import React from 'react';

const DEFAULT_MAX_RETRIES = 5;
const DEFAULT_TIME_BETWEEN_RETRIES = 1000;

// Default empty component to use as fallback
const DefaultEmptyComponent = () => /*#__PURE__*/React.createElement('div', {
  style: {
    display: 'none'
  }
});

/**
 * Attempts to load a module with a specified number of retries.
 * If the module fails to load after the specified attempts, it returns a fallback component.
 *
 * @param importFn - A function that returns a promise resolving to the module.
 * @param moduleName - The name of the module being loaded.
 * @param retriesLeft - The number of retries left (default is 5).
 * @param interval - The time interval between retries in milliseconds (default is 1000).
 * @param EmptyComponent - A React component to return if loading fails (default is a hidden div).
 * @returns A promise resolving to the loaded module or the fallback component.
 */
const attemptLazyLoadWithRetries = async function (importFn, moduleName) {
  let retriesLeft = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : DEFAULT_MAX_RETRIES;
  let interval = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : DEFAULT_TIME_BETWEEN_RETRIES;
  let EmptyComponent = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : DefaultEmptyComponent;
  try {
    const result = await importFn();
    // Ensure we have a valid component
    if (!result || typeof result.default !== 'function') {
      throw new Error('Invalid module format');
    }
    return result;
  } catch (error) {
    if (retriesLeft <= 0) {
      console.error(`Failed to load module ${moduleName}:`, error);
      // Return a proper module with a React component
      return {
        default: EmptyComponent
      };
    }
    await new Promise(resolve => {
      setTimeout(resolve, interval);
    });
    return attemptLazyLoadWithRetries(importFn, moduleName, retriesLeft - 1, interval, EmptyComponent);
  }
};

/**
 * Lazily loads a module with retry logic.
 * If the module fails to load after the specified number of attempts, it returns a fallback component.
 *
 * @param importFn - A function that returns a promise resolving to the module.
 * @param moduleName - The name of the module being loaded.
 * @param maxRetries - The maximum number of retry attempts (default is 5).
 * @param timeBetweenRetries - The time interval between retries in milliseconds (default is 1000).
 * @param EmptyComponent - A React component to return if loading fails (default is a hidden div).
 * @returns A LazyExoticComponent that can be used in React.
 */
const lazyLoadWithRetries = function (importFn, moduleName) {
  let maxRetries = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : DEFAULT_MAX_RETRIES;
  let timeBetweenRetries = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : DEFAULT_TIME_BETWEEN_RETRIES;
  let EmptyComponent = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : DefaultEmptyComponent;
  return /*#__PURE__*/React.lazy(() => attemptLazyLoadWithRetries(importFn, moduleName, maxRetries, timeBetweenRetries, EmptyComponent));
};

export { lazyLoadWithRetries };
//# sourceMappingURL=importMfe.js.map
